import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

/* ── Tenant providers ── */
import { AuthProvider } from "./contexts/AuthContext";
import { TenantProvider } from "./contexts/TenantContext";
import { ChatProvider } from "./contexts/ChatContext";
import { ToastProvider } from "./hooks/useToast";

/* ── Admin auth ── */
import { AuthProvider as AdminAuthProvider, useAuth as useAdminAuth } from "./hooks/useAdminAuth";
import { ToastProvider as AdminToastProvider } from "./components/Toast";

/* ── Tenant pages ── */
import Landing from "./pages/Landing";
import TenantLogin from "./pages/TenantLogin";
import Signup from "./pages/Signup";
import Onboarding from "./pages/Onboarding";
import TenantShell from "./components/TenantShell";
import Home from "./pages/Home";
import ConnectedBusinessesSettings from "./pages/ConnectedBusinessesSettings";
import Assistant from "./pages/Assistant";
import ManagerDetail from "./pages/ManagerDetail";
import Insights from "./pages/Insights";
import Inventory from "./pages/Inventory";
import Sales from "./pages/Sales";
import Expenses from "./pages/Expenses";
import Billing from "./pages/Billing";
import Profile from "./pages/Profile";
import SalesStrategy from "./pages/SalesStrategy";
import ERP from "./pages/ERP";
import ErpJournal from "./pages/ErpJournal";
import ErpLedger from "./pages/ErpLedger";
import ErpAging from "./pages/ErpAging";
import ErpGST from "./pages/ErpGST";
import ErpBank from "./pages/ErpBank";
import Boardroom from "./pages/Boardroom";
import MultiBranch from "./pages/MultiBranch";
import Integrations from "./pages/Integrations";
import BrandAssets from "./pages/BrandAssets";
import BrandedLogin from "./pages/BrandedLogin";
import PurchaseOrders from "./pages/PurchaseOrders";
import ReportAging from "./pages/reports/Aging";
import ReportTax from "./pages/reports/TaxReport";
import ReportTrialBalance from "./pages/reports/TrialBalance";
import ReportPnL from "./pages/reports/ProfitAndLoss";
import ReportBalanceSheet from "./pages/reports/BalanceSheet";
import GSTR1 from "./pages/reports/GSTR1";
import VoucherPayment from "./pages/vouchers/Payment";
import VoucherReceipt from "./pages/vouchers/Receipt";
import VoucherContra from "./pages/vouchers/Contra";
import VouchersList from "./pages/vouchers/VouchersList";
import VoucherDetail from "./pages/vouchers/VoucherDetail";
import CreditNotes from "./pages/notes/CreditNotes";
import CreditNoteNew from "./pages/notes/CreditNoteNew";
import CreditNoteDetail from "./pages/notes/CreditNoteDetail";
import DebitNotes from "./pages/notes/DebitNotes";
import DebitNoteNew from "./pages/notes/DebitNoteNew";
import DebitNoteDetail from "./pages/notes/DebitNoteDetail";
import Outstanding from "./pages/Outstanding";
import OutstandingDetail from "./pages/OutstandingDetail";
import Students from "./pages/school/Students";
import StudentDetail from "./pages/school/StudentDetail";
import SchoolEmployees from "./pages/school/Employees";
import EmployeeDetail from "./pages/school/EmployeeDetail";
import FeePlans from "./pages/school/FeePlans";
import FeePlanDetail from "./pages/school/FeePlanDetail";
import BillingLogs from "./pages/school/BillingLogs";
import BillingLogDetail from "./pages/school/BillingLogDetail";
import SchoolDashboard from "./pages/school/SchoolDashboard";
import Fees from "./pages/school/Fees";

/* ── Manufacturing pages ── */
import MfgDashboard from "./pages/mfg/MfgDashboard";
import MfgOrders from "./pages/mfg/MfgOrders";
import MfgProduction from "./pages/mfg/MfgProduction";
import MfgDispatch from "./pages/mfg/MfgDispatch";
import { MfgUnits, MfgSkus, MfgDestinations } from "./pages/mfg/MfgMasters";
import MfgOpeningStock from "./pages/mfg/MfgOpeningStock";
import { MfgDPR, MfgMonthlyPlan, MfgFGStock, MfgDispatchHistory, MfgProductionHistory } from "./pages/mfg/MfgReports";

/* ── Tenant contexts ── */
import { MarketingAddonProvider } from "./contexts/MarketingAddonContext";

/* ── Admin pages ── */
import LoginPage from "./pages/LoginPage";
import AdminShell from "./components/AdminShell";
import OverviewPage from "./pages/OverviewPage";
import TenantsPage from "./pages/TenantsPage";
import TenantDetailPage from "./pages/TenantDetailPage";
import OnboardTenantPage from "./pages/OnboardTenantPage";
import UsersPage from "./pages/UsersPage";
import AuditLogPage from "./pages/AuditLogPage";
import UpgradeRequestsPage from "./pages/UpgradeRequestsPage";
import MarketWatchersPage from "./pages/MarketWatchersPage";
import { SubscriptionLedgerPage, HOExpensesPage, AiProfitabilityPage, PnLPage } from "./pages/HoFinancePages";
import FeatureOverridesPage from "./pages/FeatureOverridesPage";
import PackagesPage from "./pages/PackagesPage";
import UpgradeToolPage from "./pages/UpgradeToolPage";
import AccountsFinancePage from "./pages/AccountsFinancePage";
import CountrySettingsPage from "./pages/CountrySettingsPage";

import "./App.css";

/* ── Admin auth guard ── */
function RequireAdminAuth({ children }) {
  const { ready, token } = useAdminAuth();
  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center glass-bg">
        <div className="w-10 h-10 border-4 border-purple-300 border-t-purple-600 rounded-full animate-spin" />
      </div>
    );
  }
  if (!token) return <Navigate to="/admin/login" replace />;
  return children;
}

/* ── Not found ── */
function NotFound() {
  return (
    <div className="min-h-screen bg-dark-gradient flex items-center justify-center">
      <div className="glass-card p-10 text-center max-w-md">
        <h1 className="text-4xl font-bold text-white mb-3">404</h1>
        <p className="text-white/60 mb-6">Page not found</p>
        <a
          href="/"
          className="inline-block px-6 py-3 rounded-2xl bg-gradient-to-r from-brand-purple to-brand-teal text-white font-semibold"
          data-testid="not-found-home-link"
        >
          Go Home
        </a>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ToastProvider>
        {/* ── Tenant flows (public + authenticated) ── */}
        <AuthProvider>
          <TenantProvider>
            <ChatProvider>
              <MarketingAddonProvider>
              <Routes>
                {/* Public */}
                <Route path="/" element={<Landing />} />
                <Route path="/login" element={<TenantLogin />} />
                <Route path="/t/:slug/login" element={<BrandedLogin />} />
                <Route path="/t/:slug" element={<BrandedLogin />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/onboarding" element={<Onboarding />} />

                {/* Tenant authenticated (TenantShell handles its own auth guard) */}
                <Route element={<TenantShell />}>
                  <Route path="/home" element={<Home />} />
                  <Route path="/assistant" element={<Assistant />} />
                  <Route path="/managers/:slug" element={<ManagerDetail />} />
                  <Route path="/insights" element={<Insights />} />
                  <Route path="/inventory" element={<Inventory />} />
                  <Route path="/sales" element={<Sales />} />
                  <Route path="/expenses" element={<Expenses />} />
                  <Route path="/sales-strategy" element={<SalesStrategy />} />
                  <Route path="/erp" element={<ERP />} />
                  <Route path="/erp/journal" element={<ErpJournal />} />
                  <Route path="/erp/ledger" element={<ErpLedger />} />
                  <Route path="/erp/aging" element={<ErpAging />} />
                  <Route path="/erp/gst" element={<ErpGST />} />
                  <Route path="/erp/bank" element={<ErpBank />} />
                  <Route path="/boardroom" element={<Boardroom />} />
                  <Route path="/multi-branch" element={<MultiBranch />} />
                  <Route path="/integrations" element={<Integrations />} />
                  <Route path="/brand-assets" element={<BrandAssets />} />
                  <Route path="/purchase-orders" element={<PurchaseOrders />} />
                  <Route path="/vouchers/payment" element={<VoucherPayment />} />
                  <Route path="/vouchers/receipt" element={<VoucherReceipt />} />
                  <Route path="/vouchers/contra" element={<VoucherContra />} />
                  <Route path="/vouchers/:id" element={<VoucherDetail />} />
                  <Route path="/vouchers" element={<VouchersList />} />
                  <Route path="/notes/credit/new" element={<CreditNoteNew />} />
                  <Route path="/notes/credit/:id" element={<CreditNoteDetail />} />
                  <Route path="/notes/credit" element={<CreditNotes />} />
                  <Route path="/notes/debit/new" element={<DebitNoteNew />} />
                  <Route path="/notes/debit/:id" element={<DebitNoteDetail />} />
                  <Route path="/notes/debit" element={<DebitNotes />} />
                  <Route path="/outstanding" element={<Outstanding />} />
                  <Route path="/outstanding/customers/:id" element={<OutstandingDetail />} />
                  <Route path="/outstanding/vendors/:key" element={<OutstandingDetail />} />
                  <Route path="/school/dashboard" element={<SchoolDashboard />} />
                  <Route path="/school/students" element={<Students />} />
                  <Route path="/school/students/:id" element={<StudentDetail />} />
                  <Route path="/school/employees" element={<SchoolEmployees />} />
                  <Route path="/school/employees/:id" element={<EmployeeDetail />} />
                  <Route path="/school/fee-plans" element={<FeePlans />} />
                  <Route path="/school/fee-plans/:id" element={<FeePlanDetail />} />
                  <Route path="/school/fees" element={<Fees />} />
                  <Route path="/school/billing-logs" element={<BillingLogs />} />
                  <Route path="/school/billing-logs/:id" element={<BillingLogDetail />} />
                  {/* Manufacturing */}
                  <Route path="/mfg" element={<MfgDashboard />} />
                  <Route path="/mfg/orders" element={<MfgOrders />} />
                  <Route path="/mfg/production" element={<MfgProduction />} />
                  <Route path="/mfg/dispatch" element={<MfgDispatch />} />
                  <Route path="/mfg/units" element={<MfgUnits />} />
                  <Route path="/mfg/skus" element={<MfgSkus />} />
                  <Route path="/mfg/destinations" element={<MfgDestinations />} />
                  <Route path="/mfg/opening-stock" element={<MfgOpeningStock />} />
                  <Route path="/mfg/reports/dpr" element={<MfgDPR />} />
                  <Route path="/mfg/reports/monthly" element={<MfgMonthlyPlan />} />
                  <Route path="/mfg/reports/fg-stock" element={<MfgFGStock />} />
                  <Route path="/mfg/reports/dispatch-history" element={<MfgDispatchHistory />} />
                  <Route path="/mfg/reports/production-history" element={<MfgProductionHistory />} />
                  <Route path="/reports/aging" element={<ReportAging />} />
                  <Route path="/reports/tax" element={<ReportTax />} />
                  <Route path="/reports/trial-balance" element={<ReportTrialBalance />} />
                  <Route path="/reports/pnl" element={<ReportPnL />} />
                  <Route path="/reports/balance-sheet" element={<ReportBalanceSheet />} />
                  <Route path="/reports/gstr1" element={<GSTR1 />} />
                  <Route path="/billing" element={<Billing />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/connected-businesses/settings" element={<ConnectedBusinessesSettings />} />
                </Route>

                {/* ── Admin flows ── */}
                <Route
                  path="/admin/login"
                  element={
                    <AdminAuthProvider>
                      <LoginPage />
                    </AdminAuthProvider>
                  }
                />
                <Route
                  path="/admin/*"
                  element={
                    <AdminAuthProvider>
                      <RequireAdminAuth>
                        <AdminToastProvider>
                        <Routes>
                          <Route element={<AdminShell />}>
                            <Route index element={<OverviewPage />} />
                            <Route path="tenants" element={<TenantsPage />} />
                            <Route path="tenants/new" element={<OnboardTenantPage />} />
                            <Route path="tenants/:id" element={<TenantDetailPage />} />
                            <Route path="users" element={<UsersPage />} />
                            <Route path="audit-log" element={<AuditLogPage />} />
                            <Route path="upgrade-requests" element={<UpgradeRequestsPage />} />
                            <Route path="market-watchers" element={<MarketWatchersPage />} />
                            <Route path="subscription-ledger" element={<SubscriptionLedgerPage />} />
                            <Route path="ho-expenses" element={<HOExpensesPage />} />
                            <Route path="ai-profitability" element={<AiProfitabilityPage />} />
                            <Route path="pnl" element={<PnLPage />} />
                            <Route path="feature-overrides" element={<FeatureOverridesPage />} />
                            <Route path="packages" element={<PackagesPage />} />
                            <Route path="upgrade-tool" element={<UpgradeToolPage />} />
                            <Route path="accounts-finance" element={<AccountsFinancePage />} />
                            <Route path="country-settings" element={<CountrySettingsPage />} />
                          </Route>
                          <Route path="*" element={<Navigate to="/admin" replace />} />
                        </Routes>
                        </AdminToastProvider>
                      </RequireAdminAuth>
                    </AdminAuthProvider>
                  }
                />

                {/* Catch-all */}
                <Route path="*" element={<NotFound />} />
              </Routes>
              </MarketingAddonProvider>
            </ChatProvider>
          </TenantProvider>
        </AuthProvider>
      </ToastProvider>
    </BrowserRouter>
  );
}
